<template>
  <div class="upload-wrapper">
    <el-upload
      :class="className || 'avatar-uploader'"
      ref="imageUpload"
      name="file"
      :accept="accept"
      :multiple="true"
      :drag="true"
      :action="uploadParams.host"
      :on-change="onChange"
      :data="uploadData"
      :show-file-list="false"
      :on-success="onFileSuccess"
      :on-error="onFileError"
      :on-progress="onProgress"
      :auto-upload="false"
      :disabled="isUploading"
      v-bind="$attrs"
    >
      <slot>
        <i class="el-icon-upload"></i>
        <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
      </slot>
    </el-upload>
    
    <!-- 文件列表 -->
    <div class="file-list" v-if="fileList.length > 0">
      <div class="file-list-title">上传列表 ({{ completedCount }}/{{ fileList.length }})</div>
      <div class="file-item" v-for="(item, index) in fileList" :key="index">
        <div class="file-info">
          <i :class="getFileIcon(item.name)"></i>
          <span class="file-name" :title="item.name">{{ item.name }}</span>
          <span class="file-status" :class="item.status">
            <template v-if="item.status === 'waiting'">等待中</template>
            <template v-else-if="item.status === 'uploading'">上传中</template>
            <template v-else-if="item.status === 'success'">已完成</template>
            <template v-else-if="item.status === 'error'">失败</template>
          </span>
        </div>
        <el-progress 
          v-if="item.status === 'uploading' || item.status === 'success'"
          :percentage="item.progress" 
          :status="item.status === 'success' ? 'success' : (item.status === 'error' ? 'exception' : null)"
          :stroke-width="6"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.upload-wrapper {
  width: 100%;
}

.file-list {
  margin-top: 12px;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 8px;
  
  .file-list-title {
    font-size: 13px;
    color: #606266;
    margin-bottom: 8px;
    font-weight: 500;
  }
  
  .file-item {
    padding: 8px;
    border-radius: 4px;
    background: #f5f7fa;
    margin-bottom: 8px;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    .file-info {
      display: flex;
      align-items: center;
      margin-bottom: 4px;
      
      i {
        font-size: 16px;
        margin-right: 6px;
        color: #409eff;
      }
      
      .file-name {
        flex: 1;
        font-size: 13px;
        color: #303133;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      
      .file-status {
        font-size: 12px;
        margin-left: 8px;
        flex-shrink: 0;
        
        &.waiting {
          color: #909399;
        }
        &.uploading {
          color: #409eff;
        }
        &.success {
          color: #67c23a;
        }
        &.error {
          color: #f56c6c;
        }
      }
    }
  }
}
</style>

<style lang="scss">
.el-upload-dragger {
  height: 160px !important;
  border: 2px dashed #20a0ff !important;
}
</style>

<script>
import upFile from "../views/picture/assets/scripts/upFile";

export default {
  props: {
    uploadedImg: String,
    tip: String,
    className: String,
    currentIndex: [String, Number],
    defaultImg: String,
    // 保持默认行为，素材空间可通过该属性按需放开为全部文件。
    accept: {
      type: String,
      default: "image/jpeg,image/gif,image/png,image/bmp,image/svg+xml,image/webp,video/*"
    }
  },
  data() {
    return {
      imgSrc: null, // 图片URL
      uploadParams: {
        // 上传参数
        host: "",
      },
      fileList: [], // 文件列表
      isUploading: false, // 是否正在上传
    };
  },
  computed: {
    uploadData() {
      return {
        policy: this.uploadParams.policy,
        signature: this.uploadParams.signature,
        OSSAccessKeyId: this.uploadParams.oss_accesskey_id,
        success_action_status: "200",
        name: this.filename,
        key: this.filename,
      };
    },
    completedCount() {
      return this.fileList.filter(f => f.status === 'success').length;
    }
  },
  mounted() {
    this.imgSrc = this.uploadedImg;
  },
  watch: {
    uploadedImg(val) {
      this.imgSrc = val;
    },
  },

  methods: {
    // 获取文件图标
    getFileIcon(fileName) {
      const ext = fileName.split('.').pop().toLowerCase();
      const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
      const videoExts = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv'];
      
      if (imageExts.includes(ext)) {
        return 'el-icon-picture';
      } else if (videoExts.includes(ext)) {
        return 'el-icon-video-camera';
      }
      return 'el-icon-document';
    },
    
    // 图片选中回调
    async onChange(res) {
      if (res.status === "ready") {
        // 添加到文件列表
        const fileIndex = this.fileList.length;
        this.fileList.push({
          name: res.name,
          status: 'waiting',
          progress: 0,
          file: res
        });
        
        // 开始上传
        await this.uploadFile(fileIndex);
      }
    },
    
    // 上传单个文件
    async uploadFile(index) {
      const fileItem = this.fileList[index];
      if (!fileItem) return;
      
      this.isUploading = true;
      this.$emit('uploading', true);
      fileItem.status = 'uploading';
      
      try {
        const imgSrc = await upFile(
          lib.api, 
          "ec/c/tool/upload/auth/get", 
          fileItem.file,
          (progress) => {
            // 更新进度
            this.$set(this.fileList, index, {
              ...fileItem,
              progress,
              status: progress >= 100 ? 'success' : 'uploading'
            });
          }
        );
        
        // 上传成功
        this.$set(this.fileList, index, {
          ...fileItem,
          progress: 100,
          status: 'success'
        });
        
        this.$emit("uploaded", imgSrc, fileItem.name);
      } catch (error) {
        // 上传失败
        this.$set(this.fileList, index, {
          ...fileItem,
          status: 'error'
        });
        this.$message.error(`${fileItem.name} 上传失败`);
      } finally {
        // 检查是否所有文件都上传完成
        const allDone = this.fileList.every(f => f.status === 'success' || f.status === 'error');
        if (allDone) {
          this.isUploading = false;
          this.$emit('uploading', false);
        }
      }
    },
    
    // 清空文件列表
    clearFileList() {
      this.fileList = [];
    },
    
    // 图片上传成功
    onFileSuccess(res, file, fileList) {},
    // 图片上传失败
    onFileError() {},
    // 图片上传进度
    onProgress(res) {},
  },
};
</script>
<style>
.upload-image-btn-box{
  display: grid;
}
</style>
